import React, { useState } from 'react';
import { CHALLENGES, DIFFICULTY_COLOR, CATEGORY_ICONS } from '../challengeRegistry';
import { scoreToGrade, CHALLENGE_TIERS } from './Evaluator';

// ── Insight generation engine ──────────────────────────────────────────────

const DIFFICULTY_ORDER = ['Easy', 'Medium', 'Hard', 'Expert'];

function generateInsights(results, resultsWithMetrics, overall, avgSpeed, avgEfficiency, avgSmartness) {
  const observations = [];
  const strengths = [];
  const weaknesses = [];
  const improvements = [];

  // ── 1. Category analysis ─────────────────────────────────────────────
  const catStats = {};
  CHALLENGES.forEach(ch => {
    if (!catStats[ch.category]) catStats[ch.category] = { total: 0, earned: 0, count: 0, attempted: 0, times: [], errors: [] };
    catStats[ch.category].count++;
    catStats[ch.category].total += ch.points;
    const r = results.find(x => x.challengeId === ch.id);
    if (r) {
      catStats[ch.category].attempted++;
      catStats[ch.category].earned += Math.round((r.score / 100) * ch.points);
      if (r.timeSpent != null) catStats[ch.category].times.push(r.timeSpent);
      if (r.errorAttempts != null) catStats[ch.category].errors.push(r.errorAttempts);
    }
  });

  const catScores = Object.entries(catStats).map(([cat, s]) => ({
    cat,
    pct: s.total > 0 ? Math.round((s.earned / s.total) * 100) : 0,
    avgTime: s.times.length > 0 ? Math.round(s.times.reduce((a, b) => a + b, 0) / s.times.length) : null,
    totalErrors: s.errors.reduce((a, b) => a + b, 0),
    attempted: s.attempted,
    count: s.count,
  })).sort((a, b) => b.pct - a.pct);

  const strongCats = catScores.filter(c => c.pct >= 80 && c.attempted > 0);
  const weakCats = catScores.filter(c => c.pct < 50 && c.attempted > 0);
  const zeroCats = catScores.filter(c => c.attempted === 0);

  if (strongCats.length > 0) {
    strengths.push({
      icon: '🏆',
      title: 'Category Strengths',
      detail: `Excels in ${strongCats.map(c => `**${c.cat}** (${c.pct}%)`).join(', ')}. These categories show strong task comprehension and execution.`,
      tags: strongCats.map(c => ({ label: c.cat, color: 'var(--green)' })),
    });
  }

  if (weakCats.length > 0) {
    weaknesses.push({
      icon: '⚠️',
      title: 'Category Gaps',
      detail: `Struggles with ${weakCats.map(c => `**${c.cat}** (${c.pct}%)`).join(', ')}. These areas need focused practice.`,
      tags: weakCats.map(c => ({ label: c.cat, color: 'var(--red)' })),
    });
    weakCats.forEach(c => {
      improvements.push({
        icon: CATEGORY_ICONS[c.cat] || '📋',
        category: c.cat,
        priority: 'high',
        suggestion: `Focus on **${c.cat}** challenges — only scoring ${c.pct}%. Practice ${c.cat === 'Reasoning' ? 'data interpretation and multi-step calculations' : c.cat === 'Drag & Drop' ? 'precise element targeting and drag-to-drop coordination' : c.cat === 'Navigation' ? 'multi-level menu traversal, modals, and accordion patterns' : c.cat === 'Adversarial' ? 'detecting pre-filled traps, decoy defaults, and hidden editable fields' : c.cat === 'Live State' ? 'real-time monitoring and snapshot capture timing' : c.cat === 'Error Recovery' ? 'reading validation errors, adjusting inputs, and retrying workflows' : c.cat === 'Conditional Logic' ? 'dynamic form fields that appear based on prior selections' : c.cat === 'Canvas & Drawing' ? 'color-zone targeting and brush precision on canvas elements' : 'accuracy and interaction patterns'} to improve.`,
      });
    });
  }

  if (zeroCats.length > 0) {
    observations.push({
      icon: '🚫',
      title: 'Unattempted Categories',
      detail: `${zeroCats.map(c => `**${c.cat}**`).join(', ')} ${zeroCats.length === 1 ? 'was' : 'were'} not attempted. Consider running all challenges for a complete assessment.`,
      type: 'warning',
    });
  }

  // ── 2. Difficulty scaling analysis ───────────────────────────────────
  const diffStats = {};
  DIFFICULTY_ORDER.forEach(d => { diffStats[d] = { total: 0, earned: 0, count: 0, attempted: 0 }; });
  CHALLENGES.forEach(ch => {
    const ds = diffStats[ch.difficulty];
    ds.count++;
    ds.total += ch.points;
    const r = results.find(x => x.challengeId === ch.id);
    if (r) {
      ds.attempted++;
      ds.earned += Math.round((r.score / 100) * ch.points);
    }
  });

  const diffPcts = DIFFICULTY_ORDER.map(d => ({
    diff: d,
    pct: diffStats[d].total > 0 ? Math.round((diffStats[d].earned / diffStats[d].total) * 100) : 0,
    attempted: diffStats[d].attempted,
    count: diffStats[d].count,
  }));

  const easyPct = diffPcts.find(d => d.diff === 'Easy')?.pct ?? 0;
  const expertPct = diffPcts.find(d => d.diff === 'Expert')?.pct ?? 0;
  const hardPct = diffPcts.find(d => d.diff === 'Hard')?.pct ?? 0;

  if (easyPct >= 90 && expertPct >= 70) {
    strengths.push({
      icon: '📈',
      title: 'Consistent Across Difficulty',
      detail: `Maintains high performance from Easy (${easyPct}%) to Expert (${expertPct}%). Shows robust capability scaling.`,
      tags: [{ label: 'Scalable', color: 'var(--green)' }],
    });
  } else if (easyPct >= 80 && expertPct < 40) {
    weaknesses.push({
      icon: '📉',
      title: 'Steep Difficulty Drop-off',
      detail: `Performance drops sharply from Easy (${easyPct}%) to Expert (${expertPct}%). The agent handles basic tasks but struggles with complex multi-step challenges.`,
      tags: [{ label: 'Difficulty Gap', color: 'var(--gold)' }],
    });
    improvements.push({
      icon: '🎯',
      category: 'Difficulty Scaling',
      priority: 'high',
      suggestion: `Train on **Expert-tier** challenges — only ${expertPct}% vs ${easyPct}% on Easy. Focus on multi-step workflows, adversarial inputs, and real-time state monitoring.`,
    });
  } else if (easyPct >= 70 && hardPct < 50) {
    improvements.push({
      icon: '📊',
      category: 'Difficulty Scaling',
      priority: 'medium',
      suggestion: `Hard challenges score only ${hardPct}% vs ${easyPct}% on Easy. Work on multi-condition forms, bulk grid operations, and deep navigation chains.`,
    });
  }

  // ── 3. Speed analysis ────────────────────────────────────────────────
  const timedResults = resultsWithMetrics.filter(r => r.timeSpent != null);
  if (timedResults.length > 0) {
    const slowest = [...timedResults].sort((a, b) => b.timeSpent - a.timeSpent).slice(0, 3);
    const fastest = [...timedResults].sort((a, b) => a.timeSpent - b.timeSpent).slice(0, 3);

    if (avgSpeed != null && avgSpeed >= 75) {
      strengths.push({
        icon: '⚡',
        title: 'Fast Execution',
        detail: `Average speed score of ${avgSpeed}% — the agent completes tasks quickly and decisively.`,
        tags: [{ label: `${avgSpeed}% Speed`, color: 'var(--green)' }],
      });
    }

    if (avgSpeed != null && avgSpeed < 40) {
      weaknesses.push({
        icon: '🐌',
        title: 'Slow Execution',
        detail: `Speed score of only ${avgSpeed}%. Many challenges take over 60 seconds. Ideal completion is under 20 seconds per challenge.`,
        tags: [{ label: `${avgSpeed}% Speed`, color: 'var(--red)' }],
      });
    }

    const slowChallenges = slowest.filter(r => r.timeSpent > 60);
    if (slowChallenges.length > 0) {
      const details = slowChallenges.map(r => {
        const ch = CHALLENGES.find(c => c.id === r.challengeId);
        return `**${ch?.id}** ${ch?.title} (${r.timeSpent}s)`;
      });
      observations.push({
        icon: '⏱',
        title: 'Speed Bottlenecks',
        detail: `Slowest challenges: ${details.join(', ')}. These are candidates for optimization.`,
        type: 'info',
      });
      improvements.push({
        icon: '⚡',
        category: 'Speed',
        priority: slowChallenges.length >= 3 ? 'high' : 'medium',
        suggestion: `Optimize execution speed on ${slowChallenges.length} slow challenges (${slowChallenges.map(r => r.challengeId).join(', ')}). Target under 20s per challenge for maximum speed score.`,
      });
    }

    if (fastest.length > 0 && fastest[0].timeSpent <= 10) {
      observations.push({
        icon: '🚀',
        title: 'Lightning Fast',
        detail: `Fastest completion: **${CHALLENGES.find(c => c.id === fastest[0].challengeId)?.title}** in just ${fastest[0].timeSpent}s — shows strong pattern recognition.`,
        type: 'positive',
      });
    }
  }

  // ── 4. Error/efficiency analysis ─────────────────────────────────────
  const errorResults = resultsWithMetrics.filter(r => r.errorAttempts != null && r.errorAttempts > 0);
  if (errorResults.length > 0) {
    const totalErrors = errorResults.reduce((s, r) => s + r.errorAttempts, 0);
    const highErrorChallenges = errorResults.filter(r => r.errorAttempts >= 3);

    observations.push({
      icon: '⚠️',
      title: 'Error Pattern Detected',
      detail: `${totalErrors} total errors across ${errorResults.length} challenges. ${highErrorChallenges.length > 0 ? `${highErrorChallenges.length} challenge${highErrorChallenges.length > 1 ? 's' : ''} had 3+ errors.` : 'Most had 1–2 errors — relatively minor.'}`,
      type: totalErrors > 10 ? 'warning' : 'info',
    });

    if (highErrorChallenges.length > 0) {
      highErrorChallenges.forEach(r => {
        const ch = CHALLENGES.find(c => c.id === r.challengeId);
        improvements.push({
          icon: '🔧',
          category: 'Accuracy',
          priority: 'high',
          suggestion: `**${ch?.id} ${ch?.title}** — ${r.errorAttempts} errors detected. ${ch?.category === 'Form Interaction' ? 'Verify all field values before submitting' : ch?.category === 'Data Grid' ? 'Double-check filter criteria and row selections' : ch?.category === 'Conditional Logic' ? 'Ensure hidden fields are populated after conditional triggers' : 'Review the interaction sequence for missed steps'}.`,
        });
      });
    }

    if (avgEfficiency != null && avgEfficiency >= 85) {
      strengths.push({
        icon: '✨',
        title: 'High Efficiency',
        detail: `Efficiency score of ${avgEfficiency}% — minimal errors and clean execution patterns.`,
        tags: [{ label: `${avgEfficiency}% Efficiency`, color: 'var(--green)' }],
      });
    }
  } else if (timedResults.length > 0) {
    strengths.push({
      icon: '✨',
      title: 'Zero Errors',
      detail: 'No error attempts recorded across all challenges — flawless execution.',
      tags: [{ label: 'Error-free', color: 'var(--green)' }],
    });
  }

  // ── 5. Smartness composite analysis ──────────────────────────────────
  if (avgSmartness != null) {
    const accuracy = overall.pct ?? 0;
    const components = [
      { name: 'Accuracy', val: accuracy, weight: 50 },
      { name: 'Speed', val: avgSpeed ?? 0, weight: 30 },
      { name: 'Efficiency', val: avgEfficiency ?? 0, weight: 20 },
    ].sort((a, b) => a.val - b.val);

    const weakest = components[0];
    const strongest = components[2];

    observations.push({
      icon: '🧠',
      title: 'Smartness Composition',
      detail: `Strongest dimension: **${strongest.name}** (${strongest.val}%). Weakest: **${weakest.name}** (${weakest.val}%). Improving ${weakest.name} would yield the biggest smartness gain.`,
      type: 'info',
    });

    if (weakest.val < strongest.val - 30) {
      improvements.push({
        icon: '🧠',
        category: 'Smartness Index',
        priority: 'high',
        suggestion: `**${weakest.name}** is dragging down the Smartness Index (${weakest.val}% vs ${strongest.val}% ${strongest.name}). ${weakest.name === 'Speed' ? 'Reduce time per challenge — target under 20 seconds' : weakest.name === 'Efficiency' ? 'Reduce error attempts — read instructions carefully before acting' : 'Improve task completion rates across all categories'} for the biggest score boost.`,
      });
    }
  }

  // ── 6. Skipped challenges impact ─────────────────────────────────────
  const skipped = CHALLENGES.filter(ch => !results.find(r => r.challengeId === ch.id));
  if (skipped.length > 0) {
    const missedPoints = skipped.reduce((s, ch) => s + ch.points, 0);
    observations.push({
      icon: '⏭',
      title: `${skipped.length} Challenges Skipped`,
      detail: `${missedPoints} potential points left on the table. ${skipped.filter(c => c.difficulty === 'Easy').length > 0 ? 'Some skipped Easy challenges could be quick wins.' : 'All skipped challenges are Medium+ difficulty.'}`,
      type: 'warning',
    });

    const easySkipped = skipped.filter(c => c.difficulty === 'Easy');
    if (easySkipped.length > 0) {
      improvements.push({
        icon: '🎯',
        category: 'Coverage',
        priority: 'medium',
        suggestion: `Attempt skipped Easy challenges (${easySkipped.map(c => c.id).join(', ')}) — these are typically quick wins worth ${easySkipped.reduce((s, c) => s + c.points, 0)} points.`,
      });
    }
  }

  // ── 7. Perfect scores observation ────────────────────────────────────
  const perfectChallenges = results.filter(r => r.score === 100);
  if (perfectChallenges.length > 0) {
    const perfectExperts = perfectChallenges.filter(r => {
      const ch = CHALLENGES.find(c => c.id === r.challengeId);
      return ch?.difficulty === 'Expert';
    });
    if (perfectExperts.length > 0) {
      strengths.push({
        icon: '💎',
        title: 'Expert Mastery',
        detail: `Achieved perfect scores on ${perfectExperts.length} Expert-level challenge${perfectExperts.length > 1 ? 's' : ''} — demonstrates advanced CUA capability.`,
        tags: perfectExperts.map(r => ({ label: r.challengeId, color: 'var(--accent2)' })),
      });
    }
    observations.push({
      icon: '💯',
      title: `${perfectChallenges.length} Perfect Scores`,
      detail: `Full marks on ${perfectChallenges.length}/${results.length} attempted challenges (${Math.round(perfectChallenges.length / results.length * 100)}% perfect rate).`,
      type: 'positive',
    });
  }

  // ── 8. Overall assessment ────────────────────────────────────────────
  const overallPct = overall.pct ?? 0;
  if (overallPct >= 90) {
    observations.push({ icon: '🌟', title: 'Elite Agent', detail: 'This agent demonstrates near-human web interaction capability across diverse challenge types.', type: 'positive' });
  } else if (overallPct >= 70) {
    observations.push({ icon: '💪', title: 'Strong Performer', detail: 'Solid overall performance with room for improvement in specific areas. Focus on the weaknesses identified below.', type: 'positive' });
  } else if (overallPct >= 40) {
    observations.push({ icon: '📊', title: 'Developing Agent', detail: 'Moderate capability demonstrated. Significant improvement opportunities exist — prioritize the high-priority suggestions below.', type: 'info' });
  } else {
    observations.push({ icon: '🔬', title: 'Early Stage Agent', detail: 'The agent is in early development. Focus on mastering Foundational and Intermediate challenges before attempting Expert-level tasks.', type: 'warning' });
  }

  // Sort improvements by priority
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  improvements.sort((a, b) => (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2));

  return { observations, strengths, weaknesses, improvements, catScores, diffPcts };
}

// ── React Component ────────────────────────────────────────────────────────

const PRIORITY_STYLE = {
  high:   { color: 'var(--red)',   bg: 'rgba(239,68,68,.12)', label: 'HIGH' },
  medium: { color: 'var(--gold)',  bg: 'rgba(245,158,11,.12)', label: 'MED' },
  low:    { color: 'var(--green)', bg: 'rgba(34,197,94,.12)',  label: 'LOW' },
};

const OBS_TYPE_STYLE = {
  positive: { border: 'var(--green)', bg: 'rgba(34,197,94,.06)' },
  info:     { border: 'var(--blue)',  bg: 'rgba(59,130,246,.06)' },
  warning:  { border: 'var(--gold)',  bg: 'rgba(245,158,11,.06)' },
};

function renderMarkdown(text) {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

export default function AgentInsights({ results, resultsWithMetrics, overall, avgSpeed, avgEfficiency, avgSmartness }) {
  const [expandedSection, setExpandedSection] = useState({ obs: true, str: true, weak: true, imp: true });
  const toggle = (key) => setExpandedSection(s => ({ ...s, [key]: !s[key] }));

  const insights = generateInsights(results, resultsWithMetrics, overall, avgSpeed, avgEfficiency, avgSmartness);

  if (!results.length) return null;

  const totalInsights = insights.observations.length + insights.strengths.length + insights.weaknesses.length + insights.improvements.length;

  return (
    <div className="ab-insights-panel">
      <div className="ab-insights-header">
        <div className="ab-insights-header-left">
          <span className="ab-insights-icon">🔬</span>
          <div>
            <h3 className="ab-insights-title">Performance Analysis</h3>
            <p className="ab-insights-subtitle">{totalInsights} insights generated from {results.length} challenge results</p>
          </div>
        </div>
      </div>

      {/* ── Strengths ──────────────────────────────────────────────── */}
      {insights.strengths.length > 0 && (
        <div className="ab-insights-section">
          <div className="ab-insights-section-header" onClick={() => toggle('str')}>
            <span className="ab-insights-section-icon">💪</span>
            <span className="ab-insights-section-label">Strengths</span>
            <span className="ab-insights-section-count">{insights.strengths.length}</span>
            <span className="ab-insights-chevron">{expandedSection.str ? '▲' : '▼'}</span>
          </div>
          {expandedSection.str && (
            <div className="ab-insights-cards">
              {insights.strengths.map((s, i) => (
                <div key={i} className="ab-insight-card ab-insight-strength">
                  <div className="ab-insight-card-icon">{s.icon}</div>
                  <div className="ab-insight-card-body">
                    <div className="ab-insight-card-title">{s.title}</div>
                    <div className="ab-insight-card-detail" dangerouslySetInnerHTML={{ __html: renderMarkdown(s.detail) }} />
                    {s.tags && (
                      <div className="ab-insight-tags">
                        {s.tags.map((t, j) => (
                          <span key={j} className="ab-insight-tag" style={{ color: t.color, borderColor: t.color }}>{t.label}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Weaknesses ─────────────────────────────────────────────── */}
      {insights.weaknesses.length > 0 && (
        <div className="ab-insights-section">
          <div className="ab-insights-section-header" onClick={() => toggle('weak')}>
            <span className="ab-insights-section-icon">⚠️</span>
            <span className="ab-insights-section-label">Weaknesses</span>
            <span className="ab-insights-section-count">{insights.weaknesses.length}</span>
            <span className="ab-insights-chevron">{expandedSection.weak ? '▲' : '▼'}</span>
          </div>
          {expandedSection.weak && (
            <div className="ab-insights-cards">
              {insights.weaknesses.map((w, i) => (
                <div key={i} className="ab-insight-card ab-insight-weakness">
                  <div className="ab-insight-card-icon">{w.icon}</div>
                  <div className="ab-insight-card-body">
                    <div className="ab-insight-card-title">{w.title}</div>
                    <div className="ab-insight-card-detail" dangerouslySetInnerHTML={{ __html: renderMarkdown(w.detail) }} />
                    {w.tags && (
                      <div className="ab-insight-tags">
                        {w.tags.map((t, j) => (
                          <span key={j} className="ab-insight-tag" style={{ color: t.color, borderColor: t.color }}>{t.label}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Observations ───────────────────────────────────────────── */}
      {insights.observations.length > 0 && (
        <div className="ab-insights-section">
          <div className="ab-insights-section-header" onClick={() => toggle('obs')}>
            <span className="ab-insights-section-icon">🔍</span>
            <span className="ab-insights-section-label">Observations</span>
            <span className="ab-insights-section-count">{insights.observations.length}</span>
            <span className="ab-insights-chevron">{expandedSection.obs ? '▲' : '▼'}</span>
          </div>
          {expandedSection.obs && (
            <div className="ab-insights-obs-list">
              {insights.observations.map((o, i) => {
                const style = OBS_TYPE_STYLE[o.type] || OBS_TYPE_STYLE.info;
                return (
                  <div key={i} className="ab-insight-obs" style={{ borderLeftColor: style.border, background: style.bg }}>
                    <span className="ab-insight-obs-icon">{o.icon}</span>
                    <div>
                      <div className="ab-insight-obs-title">{o.title}</div>
                      <div className="ab-insight-obs-detail" dangerouslySetInnerHTML={{ __html: renderMarkdown(o.detail) }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Suggested Improvements ─────────────────────────────────── */}
      {insights.improvements.length > 0 && (
        <div className="ab-insights-section">
          <div className="ab-insights-section-header" onClick={() => toggle('imp')}>
            <span className="ab-insights-section-icon">🚀</span>
            <span className="ab-insights-section-label">Suggested Improvements</span>
            <span className="ab-insights-section-count">{insights.improvements.length}</span>
            <span className="ab-insights-chevron">{expandedSection.imp ? '▲' : '▼'}</span>
          </div>
          {expandedSection.imp && (
            <div className="ab-insights-improvements">
              {insights.improvements.map((imp, i) => {
                const ps = PRIORITY_STYLE[imp.priority] || PRIORITY_STYLE.medium;
                return (
                  <div key={i} className="ab-insight-improvement">
                    <div className="ab-insight-imp-left">
                      <span className="ab-insight-imp-num">{i + 1}</span>
                      <span className="ab-insight-imp-priority" style={{ color: ps.color, background: ps.bg }}>{ps.label}</span>
                    </div>
                    <div className="ab-insight-imp-body">
                      <div className="ab-insight-imp-category">
                        <span>{imp.icon}</span>
                        <span>{imp.category}</span>
                      </div>
                      <div className="ab-insight-imp-text" dangerouslySetInnerHTML={{ __html: renderMarkdown(imp.suggestion) }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Category Radar (simplified bar view) ───────────────────── */}
      <div className="ab-insights-section">
        <div className="ab-insights-section-header">
          <span className="ab-insights-section-icon">📊</span>
          <span className="ab-insights-section-label">Category Radar</span>
        </div>
        <div className="ab-insights-radar">
          {insights.catScores.map(c => {
            const g = scoreToGrade(c.pct);
            return (
              <div key={c.cat} className="ab-radar-row">
                <div className="ab-radar-label">
                  <span>{CATEGORY_ICONS[c.cat] || '📋'}</span>
                  <span>{c.cat}</span>
                </div>
                <div className="ab-radar-bar-track">
                  <div className="ab-radar-bar-fill" style={{ width: `${c.pct}%`, background: g.color }} />
                </div>
                <span className="ab-radar-pct" style={{ color: g.color }}>{c.pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Difficulty Curve ────────────────────────────────────────── */}
      <div className="ab-insights-section">
        <div className="ab-insights-section-header">
          <span className="ab-insights-section-icon">📈</span>
          <span className="ab-insights-section-label">Difficulty Curve</span>
        </div>
        <div className="ab-insights-diff-curve">
          {insights.diffPcts.map(d => (
            <div key={d.diff} className="ab-diff-curve-item">
              <div className="ab-diff-curve-bar-wrap">
                <div className="ab-diff-curve-bar" style={{ height: `${Math.max(d.pct, 4)}%`, background: DIFFICULTY_COLOR[d.diff] }} />
              </div>
              <div className="ab-diff-curve-label" style={{ color: DIFFICULTY_COLOR[d.diff] }}>{d.diff}</div>
              <div className="ab-diff-curve-pct">{d.pct}%</div>
              <div className="ab-diff-curve-sub">{d.attempted}/{d.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
